import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { TextArea } from '../../components/Field';
import { ErrorNotice } from '../../components/States';
import { useToast } from '../../components/Toast';
import { createReport, type ReportReason, type ReportTarget } from '../../lib/api';
import { REPORT_REASONS } from '../../lib/categories';
import styles from './Safety.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  target: ReportTarget;
  /** "this note", "this reply", or a person's name. */
  subject: string;
}

export function ReportDialog({ open, onClose, target, subject }: Props) {
  const toast = useToast();
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');

  const report = useMutation({
    mutationFn: () => createReport(target, reason!, details),
    onSuccess: () => {
      toast.show('Report sent. A moderator will take a look.');
      close();
    },
  });

  function close() {
    setReason(null);
    setDetails('');
    report.reset();
    onClose();
  }

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (reason) report.mutate();
  };

  return (
    <Dialog open={open} onClose={close} title={`Report ${subject}`}>
      <form className={styles.form} onSubmit={submit}>
        <fieldset className={styles.reasons}>
          <legend className={styles.legend}>What's wrong?</legend>
          {REPORT_REASONS.map((r) => (
            <label key={r.value} className={styles.reason}>
              <input
                type="radio"
                name="reason"
                value={r.value}
                checked={reason === r.value}
                onChange={() => setReason(r.value)}
              />
              {r.label}
            </label>
          ))}
        </fieldset>
        <TextArea
          label="Anything moderators should know? (optional)"
          value={details}
          maxLength={1000}
          rows={3}
          onChange={(e) => setDetails(e.target.value)}
        />
        <p className={styles.fine}>Reports are private. The person you report isn't told who reported them.</p>
        {report.error && <ErrorNotice error={report.error} />}
        <div className={styles.actions}>
          <Button variant="quiet" onClick={close}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!reason || report.isPending}>
            Send report
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
